import { Box, Grid, Typography } from "@mui/material";
import { CalendarToday, EmojiEvents, Group } from "@mui/icons-material";
import { CategoryInfoProps } from "../types/PresentationFormTypes";
import { CategoryTypes } from "../../../interfaces/enums";
import { getCategoryAgeRangeDescription } from "../../../utils/dates";
/**
 * ============================================================================
 * CATEGORY INFO - KOMPONENT INFORMACJI O KATEGORII
 * ============================================================================
 *
 * CEL KOMPONENTU:
 * - Wyświetla szczegółowe informacje o kategorii prezentacji
 * - Pokazuje nazwę kategorii, ograniczenia wiekowe i liczbę zawodników
 * - Pomaga użytkownikowi zrozumieć wymagania kategorii
 * - Wizualnie wyróżnia informacje o kategorii w formularzu
 *
 * ARCHITEKTURA:
 * - Presentational Component: Tylko wyświetla dane, nie zarządza stanem
 * - Material-UI: Używa komponentów MUI do spójnego wyglądu
 * - Icons: Używa ikon do lepszej czytelności informacji
 * - Responsive: Dostosowuje się do różnych rozmiarów ekranu
 *
 * PRZEPŁYW DANYCH:
 * 1. Otrzymuje dane kategorii przez props
 * 2. Sprawdza czy kategoria istnieje (early return jeśli null)
 * 3. Wyświetla nazwę kategorii z ikoną
 * 4. Pokazuje ograniczenia liczbowe (min/max zawodników)
 * 5. Wyświetla ograniczenia wiekowe (daty urodzenia)
 *
 * SPECYFIKA WYŚWIETLANIA:
 * - SOLO: "Każdy zawodnik to osobna prezentacja"
 * - DUO/TRIO/OTHER: "min-max" (np. "2-2", "3-7")
 * - Ograniczenia wiekowe: format daty lub "Brak"
 * - Ikony: EmojiEvents (kategoria), Group (zawodnicy), CalendarToday (wiek)
 */

export default function CategoryInfo({ category }: CategoryInfoProps) {
  // ============================================================================
  // WALIDACJA WEJŚCIA
  // ============================================================================

  // Jeśli brak kategorii, nie renderuj nic (early return)
  // Zapobiega błędom z undefined data
  if (!category) return null;

  // ============================================================================
  // RENDEROWANIE INFORMACJI O KATEGORII
  // ============================================================================

  return (
    <Box sx={{ mb: 3, p: 2, backgroundColor: "#e8eaed", borderRadius: 1 }}>
      {/* ========================================================================
          NAGŁÓWEK KATEGORII
          ======================================================================== */}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <EmojiEvents sx={{ color: "#6366f1", fontSize: 18 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
          Kategoria: {category.name}
        </Typography>
      </Box>

      {/* ========================================================================
          SZCZEGÓŁY KATEGORII (RESPONSIVE GRID)
          ======================================================================== */}

      <Grid container spacing={3}>
        {/* ========================================================================
            INFORMACJE O LICZBIE ZAWODNIKÓW
            ======================================================================== */}

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Group sx={{ fontSize: 14, color: "#6c6c6c" }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
              Liczba zawodników:
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#6c6c6c" }}>
            {/* Różne formaty dla różnych typów kategorii */}
            {category.type === CategoryTypes.SOLO
              ? "Każdy zawodnik to osobna prezentacja" // SOLO: specjalny tekst
              : `${category.minPresentationSize}-${category.maxPresentationSize}`}{" "}
            {/* DUO/TRIO/OTHER: zakres */}
          </Typography>
        </Grid>

        {/* ========================================================================
            OGRANICZENIA WIEKOWE
            ======================================================================== */}

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CalendarToday sx={{ fontSize: 14, color: "#6c6c6c" }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
              Ograniczenia wiekowe:
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#6c6c6c" }}>
            {getCategoryAgeRangeDescription(category)}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
